<template>
    <div class="analytics-dashboard container-fluid py-4">
        <!-- Header -->
        <div class="row mb-4">
            <div class="col-12">
                <h1 class="mb-2">
                    <span class="text-gradient">Whale Tracker</span>
                </h1>
                <p class="text-muted mb-3">Large trades, whale movements, CEX flows and accumulation signals across Solana</p>
                <div class="d-flex gap-3 align-items-center flex-wrap">
                    <date-range-picker default-range="24h" @change="handleDateRangeChange" />
                </div>
            </div>
        </div>

        <!-- Hero Stats -->
        <div class="analytics-hero mb-4">
            <div class="analytics-hero-stat">
                <div class="hero-label">Large Trades</div>
                <div class="hero-value">{{ heroStats.tradeCount }}</div>
            </div>
            <div class="analytics-hero-stat hero-stat--green">
                <div class="hero-label">Whale Trade Volume</div>
                <div class="hero-value">${{ formatCompact(heroStats.tradeVolume) }}</div>
            </div>
            <div class="analytics-hero-stat hero-stat--blue">
                <div class="hero-label">Large Transfers</div>
                <div class="hero-value">{{ heroStats.transferCount }}</div>
            </div>
            <div class="analytics-hero-stat" :class="heroStats.netCexFlow >= 0 ? 'hero-stat--cyan' : 'hero-stat--amber'">
                <div class="hero-label">Net CEX Flow</div>
                <div class="hero-value">${{ formatCompact(Math.abs(heroStats.netCexFlow)) }}</div>
                <div class="hero-subtitle">{{ heroStats.netCexFlow >= 0 ? 'Net Inflow' : 'Net Outflow' }}</div>
            </div>
            <div class="analytics-hero-stat hero-stat--green">
                <div class="hero-label">Active Whales</div>
                <div class="hero-value">{{ heroStats.whaleCount }}</div>
            </div>
        </div>

        <!-- Tabs -->
        <div class="analytics-tabs mb-4">
            <button :class="['tab-btn', { active: activeTab === 'trades' }]" @click="switchTab('trades')">
                Large Trades
            </button>
            <button :class="['tab-btn', { active: activeTab === 'transfers' }]" @click="switchTab('transfers')">
                Large Transfers
            </button>
            <button :class="['tab-btn', { active: activeTab === 'signals' }]" @click="switchTab('signals')">
                Accumulation Signals
            </button>
            <button :class="['tab-btn', { active: activeTab === 'alerts' }]" @click="switchTab('alerts')">
                Whale Alerts
            </button>
            <button :class="['tab-btn', { active: activeTab === 'wallets' }]" @click="switchTab('wallets')">
                Top Whale Wallets
            </button>
            <button :class="['tab-btn', { active: activeTab === 'cex' }]" @click="switchTab('cex')">
                CEX Flows
            </button>
        </div>

        <!-- SECTION: Large Trades -->
        <div v-show="activeTab === 'trades'">
            <div class="section-divider">
                <span class="section-title">Recent Large Trades</span>
            </div>
            <!-- Filters -->
            <div class="d-flex gap-3 align-items-center mb-3 flex-wrap">
                <div class="filter-group">
                    <label class="filter-label">Min value ($)</label>
                    <select v-model.number="tradeMinUsd" class="filter-select" @change="loadTrades(1)">
                        <option :value="1000">$1K+</option>
                        <option :value="5000">$5K+</option>
                        <option :value="10000">$10K+</option>
                        <option :value="50000">$50K+</option>
                        <option :value="100000">$100K+</option>
                        <option :value="500000">$500K+</option>
                        <option :value="1000000">$1M+</option>
                    </select>
                </div>
                <div class="filter-group">
                    <label class="filter-label">Sort by</label>
                    <select v-model="tradeSort" class="filter-select" @change="loadTrades(1)">
                        <option value="value">Value (highest)</option>
                        <option value="time">Time (newest)</option>
                    </select>
                </div>
                <div class="filter-group">
                    <label class="filter-label">Per page</label>
                    <select v-model.number="tradePerPage" class="filter-select" @change="loadTrades(1)">
                        <option :value="25">25</option>
                        <option :value="50">50</option>
                        <option :value="100">100</option>
                    </select>
                </div>
            </div>
            <div class="analytics-row cols-1 mb-4">
                <div class="analytics-panel">
                    <div class="panel-header">
                        <div class="panel-title">Whale Trades</div>
                        <div class="panel-subtitle">{{ tradeData.total || 0 }} trades found</div>
                    </div>
                    <div class="panel-body">
                        <div class="table-responsive">
                            <table class="compass-table">
                                <thead>
                                    <tr>
                                        <th>Time</th>
                                        <th>Wallet</th>
                                        <th>Swap</th>
                                        <th class="text-end sortable" @click="toggleTradeSort">Value <span v-if="tradeSort === 'value'">&#9660;</span></th>
                                        <th>DEX</th>
                                        <th>Tx</th>
                                    </tr>
                                </thead>
                                <tbody>
                                    <tr v-for="trade in largeTrades" :key="trade.signature">
                                        <td class="text-nowrap">{{ formatTime(trade.timestamp) }}</td>
                                        <td>
                                            <a :href="'/address/' + trade.signer" class="text-gradient">
                                                {{ trade.signer_name || formatPubkey(trade.signer) }}
                                            </a>
                                            <span v-if="trade.signer_entity" class="badge-entity ms-1">{{ trade.signer_entity }}</span>
                                            <span v-for="label in (trade.signer_labels || []).slice(0, 2)" :key="label" class="badge-label ms-1">{{ label }}</span>
                                        </td>
                                        <td>
                                            <div class="swap-pair">
                                                <span class="token-badge">
                                                    <img v-if="trade.token_a.logo" :src="trade.token_a.logo" class="token-logo-sm" @error="$event.target.style.display='none'" />
                                                    {{ trade.token_a.symbol || formatPubkey(trade.token_a.mint, 4, 4) }}
                                                </span>
                                                <span class="swap-arrow">&rarr;</span>
                                                <span class="token-badge">
                                                    <img v-if="trade.token_b.logo" :src="trade.token_b.logo" class="token-logo-sm" @error="$event.target.style.display='none'" />
                                                    {{ trade.token_b.symbol || formatPubkey(trade.token_b.mint, 4, 4) }}
                                                </span>
                                            </div>
                                        </td>
                                        <td class="text-end font-mono-num">${{ formatCompact(trade.value_usd) }}</td>
                                        <td>{{ trade.exchange }}</td>
                                        <td>
                                            <a :href="'https://solscan.io/tx/' + trade.signature" target="_blank" class="tx-link">
                                                {{ formatPubkey(trade.signature, 4, 4) }}
                                            </a>
                                        </td>
                                    </tr>
                                    <tr v-if="!largeTrades.length && !loading">
                                        <td colspan="6" class="text-center text-muted py-4">No large trades found for this period</td>
                                    </tr>
                                </tbody>
                            </table>
                        </div>
                        <pagination-controls
                            v-if="tradeData.total_pages > 1"
                            :page="tradeData.page || 1"
                            :total-pages="tradeData.total_pages || 1"
                            :total="tradeData.total || 0"
                            :per-page="tradePerPage"
                            @page-change="loadTrades"
                        />
                    </div>
                </div>
            </div>
        </div>

        <!-- SECTION: Large Transfers -->
        <div v-show="activeTab === 'transfers'">
            <div class="section-divider">
                <span class="section-title">Large Transfers</span>
            </div>
            <!-- Filters -->
            <div class="d-flex gap-3 align-items-center mb-3 flex-wrap">
                <div class="filter-group">
                    <label class="filter-label">Min value ($)</label>
                    <select v-model.number="transferMinUsd" class="filter-select" @change="loadTransfers(1)">
                        <option :value="1000">$1K+</option>
                        <option :value="5000">$5K+</option>
                        <option :value="10000">$10K+</option>
                        <option :value="50000">$50K+</option>
                        <option :value="100000">$100K+</option>
                        <option :value="500000">$500K+</option>
                        <option :value="1000000">$1M+</option>
                    </select>
                </div>
                <div class="filter-group">
                    <label class="filter-label">Per page</label>
                    <select v-model.number="transferPerPage" class="filter-select" @change="loadTransfers(1)">
                        <option :value="25">25</option>
                        <option :value="50">50</option>
                        <option :value="100">100</option>
                    </select>
                </div>
            </div>
            <div class="analytics-row cols-1 mb-4">
                <div class="analytics-panel">
                    <div class="panel-header">
                        <div class="panel-title">Large Transfers</div>
                        <div class="panel-subtitle">{{ transferData.total || 0 }} transfers found</div>
                    </div>
                    <div class="panel-body">
                        <div class="table-responsive">
                            <table class="compass-table">
                                <thead>
                                    <tr>
                                        <th>Time</th>
                                        <th>Token</th>
                                        <th>From</th>
                                        <th>To</th>
                                        <th class="text-end">Value (USD)</th>
                                        <th>Tx</th>
                                    </tr>
                                </thead>
                                <tbody>
                                    <tr v-for="t in largeTransfers" :key="t.signature + t.sender">
                                        <td class="text-nowrap">{{ formatTime(t.timestamp) }}</td>
                                        <td>
                                            <a :href="'/tokens/' + t.mint" class="text-gradient">
                                                <img v-if="t.token_logo" :src="t.token_logo" class="token-logo-sm" @error="$event.target.style.display='none'" />
                                                {{ t.token_symbol || formatPubkey(t.mint, 4, 4) }}
                                            </a>
                                        </td>
                                        <td>
                                            <a :href="'/address/' + t.sender" class="text-gradient">
                                                {{ t.sender_name || formatPubkey(t.sender) }}
                                            </a>
                                            <span v-if="t.sender_entity" class="badge-entity ms-1">{{ t.sender_entity }}</span>
                                            <span v-for="label in (t.sender_labels || []).slice(0, 2)" :key="label" class="badge-label ms-1">{{ label }}</span>
                                        </td>
                                        <td>
                                            <a :href="'/address/' + t.receiver" class="text-gradient">
                                                {{ t.receiver_name || formatPubkey(t.receiver) }}
                                            </a>
                                            <span v-if="t.receiver_entity" class="badge-entity ms-1">{{ t.receiver_entity }}</span>
                                            <span v-for="label in (t.receiver_labels || []).slice(0, 2)" :key="label" class="badge-label ms-1">{{ label }}</span>
                                        </td>
                                        <td class="text-end font-mono-num">${{ formatCompact(t.amount_usd) }}</td>
                                        <td>
                                            <a v-if="t.signature" :href="'https://solscan.io/tx/' + t.signature" target="_blank" class="tx-link">
                                                {{ formatPubkey(t.signature, 4, 4) }}
                                            </a>
                                        </td>
                                    </tr>
                                    <tr v-if="!largeTransfers.length && !loading">
                                        <td colspan="6" class="text-center text-muted py-4">No large transfers found for this period</td>
                                    </tr>
                                </tbody>
                            </table>
                        </div>
                        <pagination-controls
                            v-if="transferData.total_pages > 1"
                            :page="transferData.page || 1"
                            :total-pages="transferData.total_pages || 1"
                            :total="transferData.total || 0"
                            :per-page="transferPerPage"
                            @page-change="loadTransfers"
                        />
                    </div>
                </div>
            </div>
        </div>

        <!-- SECTION: Accumulation Signals -->
        <div v-show="activeTab === 'signals'">
            <div class="section-divider">
                <span class="section-title">Whale Accumulation &amp; Distribution Signals</span>
            </div>
            <!-- Mode filter -->
            <div class="d-flex gap-3 align-items-center mb-3 flex-wrap">
                <div class="filter-group">
                    <label class="filter-label">Show</label>
                    <select v-model="signalMode" class="filter-select" @change="loadSignals">
                        <option value="all">All Signals</option>
                        <option value="accumulation">Accumulation Only</option>
                        <option value="distribution">Distribution Only</option>
                    </select>
                </div>
            </div>
            <div class="analytics-row cols-1 mb-4">
                <div class="analytics-panel">
                    <div class="panel-header">
                        <div class="panel-title">Tokens with Changing Whale Activity</div>
                        <div class="panel-subtitle">{{ signalData.comparison || '' }} &mdash; {{ signalData.signals?.length || 0 }} tokens</div>
                    </div>
                    <div class="panel-body">
                        <div class="table-responsive">
                            <table class="compass-table">
                                <thead>
                                    <tr>
                                        <th>Token</th>
                                        <th>Signal</th>
                                        <th class="text-end">Whale Volume</th>
                                        <th class="text-end">Total Volume</th>
                                        <th class="text-end">Whale %</th>
                                        <th class="text-end">Change vs Prior</th>
                                        <th class="text-end">Whale Traders</th>
                                        <th></th>
                                    </tr>
                                </thead>
                                <tbody>
                                    <tr v-for="sig in (signalData.signals || [])" :key="sig.token">
                                        <td>
                                            <a :href="'/tokens/' + sig.token" class="text-gradient">
                                                <img v-if="sig.logo" :src="sig.logo" class="token-logo-sm" @error="$event.target.style.display='none'" />
                                                {{ sig.symbol || sig.name || formatPubkey(sig.token) }}
                                            </a>
                                        </td>
                                        <td>
                                            <span :class="getSignalClass(sig.signal)">
                                                {{ getSignalLabel(sig.signal) }}
                                            </span>
                                        </td>
                                        <td class="text-end font-mono-num">${{ formatCompact(sig.whale_volume) }}</td>
                                        <td class="text-end font-mono-num">${{ formatCompact(sig.total_volume) }}</td>
                                        <td class="text-end font-mono-num">{{ sig.whale_percent }}%</td>
                                        <td class="text-end">
                                            <span :class="sig.whale_change_pct >= 0 ? 'pct-positive' : 'pct-negative'">
                                                {{ sig.whale_change_pct >= 0 ? '+' : '' }}{{ sig.whale_change_pct }}%
                                            </span>
                                        </td>
                                        <td class="text-end font-mono-num">{{ formatCompact(sig.whale_traders) }}</td>
                                        <td>
                                            <button class="btn-detail" @click="openSignalDetail(sig.token, sig.symbol)" title="View whale traders">
                                                Details
                                            </button>
                                        </td>
                                    </tr>
                                    <tr v-if="!(signalData.signals || []).length && !loading">
                                        <td colspan="8" class="text-center text-muted py-4">No accumulation signals found for this period</td>
                                    </tr>
                                </tbody>
                            </table>
                        </div>
                    </div>
                </div>
            </div>

            <!-- Signal Detail Modal -->
            <div v-if="signalDetailOpen" class="modal-backdrop" @click.self="signalDetailOpen = false">
                <div class="modal-content">
                    <div class="modal-header">
                        <h5>Whale Traders for {{ signalDetailSymbol }}</h5>
                        <button class="btn-close-modal" @click="signalDetailOpen = false">&times;</button>
                    </div>
                    <div class="modal-body">
                        <div v-if="signalDetailLoading" class="text-center py-4">
                            <div class="spinner-border text-primary spinner-border-sm" role="status"></div>
                        </div>
                        <div v-else>
                            <table class="compass-table" v-if="signalDetailData.length">
                                <thead>
                                    <tr>
                                        <th>#</th>
                                        <th>Wallet</th>
                                        <th class="text-end">Volume</th>
                                        <th class="text-end">Trades</th>
                                    </tr>
                                </thead>
                                <tbody>
                                    <tr v-for="(w, idx) in signalDetailData" :key="w.address">
                                        <td>{{ idx + 1 }}</td>
                                        <td>
                                            <a :href="'/address/' + w.address" class="text-gradient">
                                                {{ w.name || formatPubkey(w.address) }}
                                            </a>
                                            <span v-if="w.entity" class="badge-entity ms-1">{{ w.entity }}</span>
                                            <span v-for="label in (w.labels || []).slice(0, 2)" :key="label" class="badge-label ms-1">{{ label }}</span>
                                        </td>
                                        <td class="text-end font-mono-num">${{ formatCompact(w.volume_usd) }}</td>
                                        <td class="text-end font-mono-num">{{ w.tx_count }}</td>
                                    </tr>
                                </tbody>
                            </table>
                            <p v-else class="text-muted text-center py-3">No whale traders found</p>
                        </div>
                    </div>
                </div>
            </div>
        </div>

        <!-- SECTION: Whale Alerts -->
        <div v-show="activeTab === 'alerts'">
            <div class="section-divider">
                <span class="section-title">Whale Alerts</span>
                <span class="section-subtitle ms-2 text-muted small">Top token holders' recent trades</span>
            </div>
            <div class="d-flex gap-3 align-items-center mb-3 flex-wrap">
                <div class="filter-group">
                    <label class="filter-label">Min holding ($)</label>
                    <select v-model.number="alertMinHolding" class="filter-select" @change="loadAlerts">
                        <option :value="10000">$10K+</option>
                        <option :value="50000">$50K+</option>
                        <option :value="100000">$100K+</option>
                        <option :value="500000">$500K+</option>
                    </select>
                </div>
                <span class="text-muted small" v-if="alertData.tracked_tokens">
                    Tracking {{ alertData.tracked_whales }} whales across {{ alertData.tracked_tokens }} tokens
                </span>
            </div>
            <div class="analytics-row cols-1 mb-4">
                <div class="analytics-panel">
                    <div class="panel-header">
                        <div class="panel-title">Recent Whale Trades</div>
                        <div class="panel-subtitle">{{ alertData.count || 0 }} trades</div>
                    </div>
                    <div class="panel-body">
                        <div class="table-responsive">
                            <table class="compass-table">
                                <thead>
                                    <tr>
                                        <th>Time</th>
                                        <th>Whale</th>
                                        <th>Known For</th>
                                        <th>Swap</th>
                                        <th class="text-end">Value</th>
                                        <th>DEX</th>
                                        <th>Tx</th>
                                    </tr>
                                </thead>
                                <tbody>
                                    <tr v-for="alert in (alertData.alerts || [])" :key="alert.signature">
                                        <td class="text-nowrap">{{ formatTime(alert.timestamp) }}</td>
                                        <td>
                                            <a :href="'/address/' + alert.signer" class="text-gradient">
                                                {{ alert.signer_name || formatPubkey(alert.signer) }}
                                            </a>
                                            <span v-if="alert.signer_entity" class="badge-entity ms-1">{{ alert.signer_entity }}</span>
                                        </td>
                                        <td>
                                            <span v-for="sym in (alert.whale_known_for || [])" :key="sym" class="badge-token ms-1">{{ sym }}</span>
                                            <span v-if="alert.whale_holding_usd" class="text-muted small ms-1">${{ formatCompact(alert.whale_holding_usd) }}</span>
                                        </td>
                                        <td>
                                            <div class="swap-pair">
                                                <span class="token-badge">
                                                    <img v-if="alert.token_a.logo" :src="alert.token_a.logo" class="token-logo-sm" @error="$event.target.style.display='none'" />
                                                    {{ alert.token_a.symbol || formatPubkey(alert.token_a.mint, 4, 4) }}
                                                </span>
                                                <span class="swap-arrow">&rarr;</span>
                                                <span class="token-badge">
                                                    <img v-if="alert.token_b.logo" :src="alert.token_b.logo" class="token-logo-sm" @error="$event.target.style.display='none'" />
                                                    {{ alert.token_b.symbol || formatPubkey(alert.token_b.mint, 4, 4) }}
                                                </span>
                                            </div>
                                        </td>
                                        <td class="text-end font-mono-num">${{ formatCompact(alert.value_usd) }}</td>
                                        <td>{{ alert.exchange }}</td>
                                        <td>
                                            <a :href="'https://solscan.io/tx/' + alert.signature" target="_blank" class="tx-link">
                                                {{ formatPubkey(alert.signature, 4, 4) }}
                                            </a>
                                        </td>
                                    </tr>
                                    <tr v-if="!(alertData.alerts || []).length && !loading">
                                        <td colspan="7" class="text-center text-muted py-4">No whale alerts found for this period</td>
                                    </tr>
                                </tbody>
                            </table>
                        </div>
                    </div>
                </div>
            </div>
        </div>

        <!-- SECTION: Top Whale Wallets -->
        <div v-show="activeTab === 'wallets'">
            <div class="section-divider">
                <span class="section-title">Top Whale Wallets by Swap Volume</span>
            </div>
            <!-- Filters -->
            <div class="d-flex gap-3 align-items-center mb-3 flex-wrap">
                <div class="filter-group">
                    <label class="filter-label">Min volume ($)</label>
                    <select v-model.number="walletMinVolume" class="filter-select" @change="loadWhaleWallets(1)">
                        <option :value="10000">$10K+</option>
                        <option :value="50000">$50K+</option>
                        <option :value="100000">$100K+</option>
                        <option :value="500000">$500K+</option>
                        <option :value="1000000">$1M+</option>
                    </select>
                </div>
                <div class="filter-group">
                    <label class="filter-label">Filter bots</label>
                    <select v-model="walletFilterBots" class="filter-select" @change="loadWhaleWallets(1)">
                        <option value="true">Hide likely bots</option>
                        <option value="false">Show all</option>
                    </select>
                </div>
                <div class="filter-group" v-if="walletFilterBots === 'true'">
                    <label class="filter-label">Max tx/day</label>
                    <select v-model.number="walletMaxTxPerDay" class="filter-select" @change="loadWhaleWallets(1)">
                        <option :value="100">100</option>
                        <option :value="250">250</option>
                        <option :value="500">500</option>
                        <option :value="1000">1,000</option>
                        <option :value="5000">5,000</option>
                        <option :value="10000">10,000</option>
                    </select>
                </div>
                <div class="filter-group">
                    <label class="filter-label">Per page</label>
                    <select v-model.number="walletPerPage" class="filter-select" @change="loadWhaleWallets(1)">
                        <option :value="25">25</option>
                        <option :value="50">50</option>
                        <option :value="100">100</option>
                    </select>
                </div>
            </div>
            <div class="analytics-row cols-1 mb-4">
                <div class="analytics-panel">
                    <div class="panel-header">
                        <div class="panel-title">Most Active Whale Wallets</div>
                        <div class="panel-subtitle">{{ walletData.total || 0 }} wallets &mdash; Total volume: ${{ formatCompact(walletData.total_volume_usd || 0) }}</div>
                    </div>
                    <div class="panel-body">
                        <div class="table-responsive">
                            <table class="compass-table">
                                <thead>
                                    <tr>
                                        <th>#</th>
                                        <th>Wallet</th>
                                        <th class="text-end">Swap Volume</th>
                                        <th class="text-end">Fees (SOL)</th>
                                        <th class="text-end">Tips (SOL)</th>
                                        <th class="text-end">Transactions</th>
                                        <th class="text-end">Tx/Day</th>
                                        <th class="text-end">Programs</th>
                                    </tr>
                                </thead>
                                <tbody>
                                    <tr v-for="(w, idx) in whaleWallets" :key="w.address">
                                        <td><span class="rank-badge">{{ ((walletData.page || 1) - 1) * walletPerPage + idx + 1 }}</span></td>
                                        <td>
                                            <a :href="'/address/' + w.address" class="text-gradient">
                                                {{ w.name || formatPubkey(w.address) }}
                                            </a>
                                            <span v-if="w.entity" class="badge-entity ms-1">{{ w.entity }}</span>
                                            <span v-for="label in (w.labels || []).slice(0, 2)" :key="label" class="badge-label ms-1">{{ label }}</span>
                                        </td>
                                        <td class="text-end font-mono-num">${{ formatCompact(w.swap_volume_usd) }}</td>
                                        <td class="text-end font-mono-num">{{ w.total_fees?.toFixed(4) }}</td>
                                        <td class="text-end font-mono-num">{{ w.total_tips?.toFixed(4) }}</td>
                                        <td class="text-end font-mono-num">{{ formatCompact(w.tx_count) }}</td>
                                        <td class="text-end font-mono-num">{{ w.tx_per_day }}</td>
                                        <td class="text-end font-mono-num">{{ w.programs_used }}</td>
                                    </tr>
                                    <tr v-if="!whaleWallets.length && !loading">
                                        <td colspan="8" class="text-center text-muted py-4">No whale wallets found for this period</td>
                                    </tr>
                                </tbody>
                            </table>
                        </div>
                        <pagination-controls
                            v-if="walletData.total_pages > 1"
                            :page="walletData.page || 1"
                            :total-pages="walletData.total_pages || 1"
                            :total="walletData.total || 0"
                            :per-page="walletPerPage"
                            @page-change="loadWhaleWallets"
                        />
                    </div>
                </div>
            </div>
        </div>

        <!-- SECTION: CEX Flows -->
        <div v-show="activeTab === 'cex'">
            <div class="section-divider">
                <span class="section-title">CEX Flow Tracker</span>
                <span class="section-subtitle ms-2 text-muted small">Tracks labelled exchange wallets via transfer sender/receiver</span>
            </div>

            <!-- CEX Summary Cards -->
            <div class="analytics-row cols-3 mb-4" v-if="cexData.summary">
                <div class="analytics-panel">
                    <div class="panel-body text-center py-3">
                        <div class="metric-label">Total Inflow (deposits)</div>
                        <div class="metric-value text-success font-mono-num">${{ formatCompact(cexData.summary.inflow_usd) }}</div>
                    </div>
                </div>
                <div class="analytics-panel">
                    <div class="panel-body text-center py-3">
                        <div class="metric-label">Total Outflow (withdrawals)</div>
                        <div class="metric-value text-danger font-mono-num">${{ formatCompact(cexData.summary.outflow_usd) }}</div>
                    </div>
                </div>
                <div class="analytics-panel">
                    <div class="panel-body text-center py-3">
                        <div class="metric-label">Net Flow</div>
                        <div class="metric-value font-mono-num" :class="(cexData.summary.net_flow_usd || 0) >= 0 ? 'text-success' : 'text-danger'">
                            {{ (cexData.summary.net_flow_usd || 0) >= 0 ? '+' : '' }}${{ formatCompact(Math.abs(cexData.summary.net_flow_usd || 0)) }}
                        </div>
                        <div class="metric-subtitle">{{ cexData.summary.direction === 'net_inflow' ? 'Flowing into exchanges' : 'Flowing out of exchanges' }}</div>
                    </div>
                </div>
            </div>

            <!-- CEX Flow Chart -->
            <div class="analytics-row cols-1 mb-4">
                <div class="analytics-panel">
                    <div class="panel-header">
                        <div class="panel-title">CEX Flow Over Time</div>
                    </div>
                    <div class="panel-body chart-tall">
                        <canvas ref="cexFlowChart"></canvas>
                        <p v-if="!(cexData.time_series || []).length && !loading" class="text-muted text-center py-4">No time series data available</p>
                    </div>
                </div>
            </div>

            <!-- Two column: Exchange + Token breakdown -->
            <div class="analytics-row cols-2 mb-4">
                <!-- Per Exchange Breakdown -->
                <div class="analytics-panel">
                    <div class="panel-header">
                        <div class="panel-title">Flow by Exchange</div>
                        <div class="panel-subtitle">{{ (cexData.by_exchange || []).length }} exchanges tracked</div>
                    </div>
                    <div class="panel-body">
                        <div class="table-responsive">
                            <table class="compass-table">
                                <thead>
                                    <tr>
                                        <th>Exchange</th>
                                        <th class="text-end">Inflow</th>
                                        <th class="text-end">Outflow</th>
                                        <th class="text-end">Net Flow</th>
                                        <th>Direction</th>
                                    </tr>
                                </thead>
                                <tbody>
                                    <tr v-for="ex in (cexData.by_exchange || [])" :key="ex.entity">
                                        <td>
                                            <span class="text-gradient">{{ ex.entity }}</span>
                                            <span v-if="ex.wallets > 1" class="badge-label ms-1">{{ ex.wallets }} wallets</span>
                                        </td>
                                        <td class="text-end font-mono-num text-success">${{ formatCompact(ex.inflow_usd) }}</td>
                                        <td class="text-end font-mono-num text-danger">${{ formatCompact(ex.outflow_usd) }}</td>
                                        <td class="text-end font-mono-num" :class="ex.net_flow_usd >= 0 ? 'text-success' : 'text-danger'">
                                            {{ ex.net_flow_usd >= 0 ? '+' : '' }}${{ formatCompact(Math.abs(ex.net_flow_usd)) }}
                                        </td>
                                        <td>
                                            <span :class="ex.direction === 'net_inflow' ? 'pct-positive' : 'pct-negative'">
                                                {{ ex.direction === 'net_inflow' ? 'Inflow' : 'Outflow' }}
                                            </span>
                                        </td>
                                    </tr>
                                    <tr v-if="!(cexData.by_exchange || []).length && !loading">
                                        <td colspan="5" class="text-center text-muted py-4">No CEX flow data available</td>
                                    </tr>
                                </tbody>
                            </table>
                        </div>
                    </div>
                </div>

                <!-- Per Token Breakdown -->
                <div class="analytics-panel">
                    <div class="panel-header">
                        <div class="panel-title">Flow by Token</div>
                        <div class="panel-subtitle">{{ (cexData.by_token || []).length }} tokens</div>
                    </div>
                    <div class="panel-body">
                        <div class="table-responsive">
                            <table class="compass-table">
                                <thead>
                                    <tr>
                                        <th>Token</th>
                                        <th class="text-end">Inflow</th>
                                        <th class="text-end">Outflow</th>
                                        <th class="text-end">Net Flow</th>
                                        <th>Direction</th>
                                    </tr>
                                </thead>
                                <tbody>
                                    <tr v-for="tok in (cexData.by_token || [])" :key="tok.mint">
                                        <td>
                                            <a :href="'/tokens/' + tok.mint" class="text-gradient">
                                                <img v-if="tok.logo" :src="tok.logo" class="token-logo-sm" @error="$event.target.style.display='none'" />
                                                {{ tok.symbol }}
                                            </a>
                                        </td>
                                        <td class="text-end font-mono-num text-success">${{ formatCompact(tok.inflow_usd) }}</td>
                                        <td class="text-end font-mono-num text-danger">${{ formatCompact(tok.outflow_usd) }}</td>
                                        <td class="text-end font-mono-num" :class="tok.net_flow_usd >= 0 ? 'text-success' : 'text-danger'">
                                            {{ tok.net_flow_usd >= 0 ? '+' : '' }}${{ formatCompact(Math.abs(tok.net_flow_usd)) }}
                                        </td>
                                        <td>
                                            <span :class="tok.direction === 'net_inflow' ? 'pct-positive' : 'pct-negative'">
                                                {{ tok.direction === 'net_inflow' ? 'Inflow' : 'Outflow' }}
                                            </span>
                                        </td>
                                    </tr>
                                    <tr v-if="!(cexData.by_token || []).length && !loading">
                                        <td colspan="5" class="text-center text-muted py-4">No token flow data available</td>
                                    </tr>
                                </tbody>
                            </table>
                        </div>
                    </div>
                </div>
            </div>
        </div>

        <!-- Loading Overlay -->
        <div v-if="loading" class="text-center py-4">
            <div class="spinner-border text-primary" role="status">
                <span class="visually-hidden">Loading...</span>
            </div>
        </div>
    </div>
</template>

<script>
import { formatCompact, formatSOL, formatPubkey, formatUSD } from '../composables/useFormatters';
import { Chart, registerables } from 'chart.js';
import { colors, getStackedBarChartOptions, getThemeAwareColors } from '../composables/useChartConfig';

Chart.register(...registerables);

export default {
    name: 'WhaleTracker',

    data() {
        return {
            currentRange: '24h',
            activeTab: 'trades',
            loading: false,
            refreshInterval: null,

            // Trade filters
            tradeMinUsd: 10000,
            tradeSort: 'value',
            tradePerPage: 100,

            // Transfer filters
            transferMinUsd: 10000,
            transferPerPage: 100,

            // Signal filters
            signalMode: 'all',

            // Alert filters
            alertMinHolding: 50000,

            // Wallet filters
            walletMinVolume: 100000,
            walletFilterBots: 'false',
            walletMaxTxPerDay: 5000,
            walletPerPage: 100,

            // Data stores
            tradeData: {},
            largeTrades: [],
            transferData: {},
            largeTransfers: [],
            signalData: {},
            alertData: {},
            walletData: {},
            whaleWallets: [],
            cexData: {},

            // Signal detail modal
            signalDetailOpen: false,
            signalDetailSymbol: '',
            signalDetailLoading: false,
            signalDetailData: [],

            // Charts
            charts: {},
        };
    },

    computed: {
        heroStats() {
            return {
                tradeCount: this.tradeData.total || 0,
                tradeVolume: this.largeTrades.reduce((sum, t) => sum + (t.value_usd || 0), 0),
                transferCount: this.transferData.total || 0,
                netCexFlow: this.cexData?.summary?.net_flow_usd || 0,
                whaleCount: this.walletData.total || 0,
            };
        },
    },

    mounted() {
        this.loadAllData();
        this.refreshInterval = setInterval(() => this.loadAllData(), 60000);
    },

    beforeUnmount() {
        Object.values(this.charts).forEach(c => c?.destroy());
        if (this.refreshInterval) clearInterval(this.refreshInterval);
    },

    methods: {
        formatCompact,
        formatSOL,
        formatPubkey,
        formatUSD,

        handleDateRangeChange(data) {
            this.currentRange = data.range;
            this.loadAllData();
        },

        switchTab(tab) {
            this.activeTab = tab;
        },

        formatTime(ts) {
            if (!ts) return '-';
            const d = new Date(ts);
            return d.toLocaleDateString('en-US', { month: 'short', day: 'numeric' }) + ' ' +
                   d.toLocaleTimeString('en-US', { hour: '2-digit', minute: '2-digit', hour12: false });
        },

        toggleTradeSort() {
            this.tradeSort = this.tradeSort === 'value' ? 'time' : 'value';
            this.loadTrades(1);
        },

        getSignalClass(signal) {
            const map = {
                'strong_accumulation': 'signal-badge signal-strong-up',
                'accumulation': 'signal-badge signal-up',
                'distribution': 'signal-badge signal-down',
                'strong_distribution': 'signal-badge signal-strong-down',
            };
            return map[signal] || 'signal-badge';
        },

        getSignalLabel(signal) {
            const map = {
                'strong_accumulation': 'Strong Accumulation',
                'accumulation': 'Accumulation',
                'distribution': 'Distribution',
                'strong_distribution': 'Strong Distribution',
            };
            return map[signal] || signal;
        },

        async loadAllData() {
            this.loading = true;
            try {
                await Promise.all([
                    this.loadTrades(1),
                    this.loadTransfers(1),
                    this.loadSignals(),
                    this.loadAlerts(),
                    this.loadWhaleWallets(1),
                    this.loadCexFlows(),
                ]);
            } finally {
                this.loading = false;
            }
        },

        async loadTrades(page = 1) {
            try {
                const params = new URLSearchParams({
                    range: this.currentRange,
                    page,
                    per_page: this.tradePerPage,
                    min_usd: this.tradeMinUsd,
                    sort: this.tradeSort,
                    dir: 'desc',
                });
                const res = await fetch(`/analytics/api/whale-trades?${params}`);
                const data = await res.json();
                this.tradeData = data;
                this.largeTrades = data.trades || [];
            } catch (e) {
                console.error('Failed to load whale trades:', e);
            }
        },

        async loadTransfers(page = 1) {
            try {
                const params = new URLSearchParams({
                    range: this.currentRange,
                    page,
                    per_page: this.transferPerPage,
                    min_usd: this.transferMinUsd,
                });
                const res = await fetch(`/analytics/api/whale-transfers?${params}`);
                const data = await res.json();
                this.transferData = data;
                this.largeTransfers = data.transfers || [];
            } catch (e) {
                console.error('Failed to load whale transfers:', e);
            }
        },

        async loadSignals() {
            try {
                const params = new URLSearchParams({
                    range: this.currentRange,
                    limit: 50,
                    mode: this.signalMode,
                });
                const res = await fetch(`/analytics/api/whale-signals?${params}`);
                const data = await res.json();
                this.signalData = data;
            } catch (e) {
                console.error('Failed to load signals:', e);
            }
        },

        async openSignalDetail(mint, symbol) {
            this.signalDetailOpen = true;
            this.signalDetailSymbol = symbol || formatPubkey(mint);
            this.signalDetailLoading = true;
            this.signalDetailData = [];
            try {
                const params = new URLSearchParams({
                    mint,
                    range: this.currentRange,
                    limit: 50,
                });
                const res = await fetch(`/analytics/api/whale-signal-detail?${params}`);
                const data = await res.json();
                this.signalDetailData = data.wallets || [];
            } catch (e) {
                console.error('Failed to load signal detail:', e);
            } finally {
                this.signalDetailLoading = false;
            }
        },

        async loadAlerts() {
            try {
                const params = new URLSearchParams({
                    range: this.currentRange,
                    limit: 100,
                    min_holding_usd: this.alertMinHolding,
                });
                const res = await fetch(`/analytics/api/whale-alerts?${params}`);
                const data = await res.json();
                this.alertData = data;
            } catch (e) {
                console.error('Failed to load whale alerts:', e);
            }
        },

        async loadWhaleWallets(page = 1) {
            try {
                const params = new URLSearchParams({
                    range: this.currentRange,
                    page,
                    per_page: this.walletPerPage,
                    min_volume: this.walletMinVolume,
                    filter_bots: this.walletFilterBots,
                    max_tx_per_day: this.walletMaxTxPerDay,
                });
                const res = await fetch(`/analytics/api/whale-wallets?${params}`);
                const data = await res.json();
                this.walletData = data;
                this.whaleWallets = data.wallets || [];
            } catch (e) {
                console.error('Failed to load whale wallets:', e);
            }
        },

        async loadCexFlows() {
            try {
                const res = await fetch(`/analytics/api/whale-cex-flows?range=${this.currentRange}`);
                const data = await res.json();
                this.cexData = data;
                this.$nextTick(() => this.renderCexChart());
            } catch (e) {
                console.error('Failed to load CEX flows:', e);
            }
        },

        renderCexChart() {
            const canvas = this.$refs.cexFlowChart;
            if (!canvas) return;

            if (this.charts.cexFlow) {
                this.charts.cexFlow.destroy();
                this.charts.cexFlow = null;
            }

            const timeSeries = this.cexData.time_series || [];
            if (!timeSeries.length) return;

            const labels = timeSeries.map(p => {
                const d = new Date(p.timestamp);
                return d.toLocaleDateString('en-US', { month: 'short', day: 'numeric' }) + ' ' +
                       d.toLocaleTimeString('en-US', { hour: '2-digit', minute: '2-digit', hour12: false });
            });

            const chartOptions = getStackedBarChartOptions({
                formatCallback: (v) => '$' + this.formatCompact(Math.abs(v)),
                useDynamicColors: true,
            });
            chartOptions.plugins.tooltip = {
                callbacks: {
                    label: (ctx) => {
                        const val = Math.abs(ctx.raw);
                        return `${ctx.dataset.label}: $${this.formatCompact(val)}`;
                    },
                },
            };
            // Ensure chart uses available space properly
            chartOptions.maintainAspectRatio = false;

            this.charts.cexFlow = new Chart(canvas, {
                type: 'bar',
                data: {
                    labels,
                    datasets: [
                        {
                            label: 'Inflow (deposits)',
                            data: timeSeries.map(p => p.inflow_usd),
                            backgroundColor: colors.positive + 'B3',
                        },
                        {
                            label: 'Outflow (withdrawals)',
                            data: timeSeries.map(p => -p.outflow_usd),
                            backgroundColor: colors.negative + 'B3',
                        },
                    ],
                },
                options: chartOptions,
            });
        },
    },
};
</script>

<style scoped>
.swap-pair {
    display: flex;
    align-items: center;
    gap: 4px;
}
.swap-arrow {
    color: #6b7280;
    font-size: 0.85rem;
}
.token-badge {
    display: inline-flex;
    align-items: center;
    gap: 4px;
}
.token-logo-sm {
    width: 18px;
    height: 18px;
    border-radius: 50%;
    vertical-align: middle;
}
.badge-entity {
    display: inline-block;
    padding: 1px 6px;
    border-radius: 4px;
    font-size: 0.7rem;
    background: rgba(139, 92, 246, 0.2);
    color: #a78bfa;
}
.badge-label {
    display: inline-block;
    padding: 1px 6px;
    border-radius: 4px;
    font-size: 0.7rem;
    background: rgba(16, 247, 207, 0.15);
    color: #10f7cf;
}
.badge-token {
    display: inline-block;
    padding: 1px 6px;
    border-radius: 4px;
    font-size: 0.7rem;
    font-weight: 600;
    background: rgba(139, 92, 246, 0.15);
    color: #a78bfa;
}
.tx-link {
    color: #6b7280;
    text-decoration: none;
    font-family: monospace;
    font-size: 0.85rem;
}
.tx-link:hover {
    color: #8b5cf6;
}
.signal-badge {
    display: inline-block;
    padding: 2px 8px;
    border-radius: 4px;
    font-size: 0.75rem;
    font-weight: 600;
}
.signal-strong-up {
    background: rgba(34, 197, 94, 0.2);
    color: #22c55e;
}
.signal-up {
    background: rgba(34, 197, 94, 0.1);
    color: #4ade80;
}
.signal-down {
    background: rgba(239, 68, 68, 0.1);
    color: #f87171;
}
.signal-strong-down {
    background: rgba(239, 68, 68, 0.2);
    color: #ef4444;
}
.rank-badge {
    display: inline-flex;
    align-items: center;
    justify-content: center;
    width: 24px;
    height: 24px;
    border-radius: 50%;
    background: rgba(139, 92, 246, 0.2);
    color: #a78bfa;
    font-size: 0.75rem;
    font-weight: 600;
}

/* Filters */
.filter-group {
    display: flex;
    align-items: center;
    gap: 6px;
}
.filter-label {
    font-size: 0.8rem;
    color: #9ca3af;
    white-space: nowrap;
}
.filter-select {
    background: rgba(30, 30, 46, 0.8);
    border: 1px solid rgba(139, 92, 246, 0.3);
    border-radius: 6px;
    color: #e5e7eb;
    padding: 4px 8px;
    font-size: 0.8rem;
    cursor: pointer;
}
.filter-select:focus {
    outline: none;
    border-color: #8b5cf6;
}

.sortable {
    cursor: pointer;
    user-select: none;
}
.sortable:hover {
    color: #8b5cf6;
}

.btn-detail {
    background: rgba(139, 92, 246, 0.15);
    border: 1px solid rgba(139, 92, 246, 0.3);
    border-radius: 4px;
    color: #a78bfa;
    padding: 2px 8px;
    font-size: 0.75rem;
    cursor: pointer;
}
.btn-detail:hover {
    background: rgba(139, 92, 246, 0.3);
}

/* Modal */
.modal-backdrop {
    position: fixed;
    top: 0;
    left: 0;
    right: 0;
    bottom: 0;
    background: rgba(0, 0, 0, 0.6);
    z-index: 1000;
    display: flex;
    align-items: center;
    justify-content: center;
}
.modal-content {
    background: #1e1e2e;
    border: 1px solid rgba(139, 92, 246, 0.3);
    border-radius: 12px;
    width: 90%;
    max-width: 700px;
    max-height: 80vh;
    overflow: auto;
}
.modal-header {
    display: flex;
    justify-content: space-between;
    align-items: center;
    padding: 16px 20px;
    border-bottom: 1px solid rgba(139, 92, 246, 0.2);
}
.modal-header h5 {
    margin: 0;
    color: #e5e7eb;
}
.btn-close-modal {
    background: none;
    border: none;
    color: #9ca3af;
    font-size: 1.5rem;
    cursor: pointer;
    line-height: 1;
}
.btn-close-modal:hover {
    color: #e5e7eb;
}
.modal-body {
    padding: 16px 20px;
}
</style>
